import React from 'react';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import SaveStatus from './SaveStatus';

/**
 * Sticky bottom action bar for wizard steps. `helperText` shows a short status
 * line above the buttons (e.g. what is still missing); `onHelperClick` makes
 * it a button that takes the user there.
 */
const StepFooter = ({
  onBack,
  onNext,
  nextLabel = 'Save & continue',
  backLabel = 'Back',
  isFirst = false,
  isSubmitting = false,
  saveStatus,
  lastSavedAt,
  onRetrySave,
  nextDisabled = false,
  nextVariant = 'primary',
  helperText,
  helperTone = 'muted',
  onHelperClick
}) => (
  <div
    className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur"
    style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
  >
    {helperText ? (
      <div className="mx-auto max-w-4xl px-4 pt-2 sm:px-6">
        {onHelperClick ? (
          <button
            type="button"
            onClick={onHelperClick}
            className={`w-full rounded-md px-2 py-1 text-center text-sm font-medium underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-right ${helperTone === 'error' ? 'text-red-700' : 'text-gray-600'}`}
          >
            {helperText}
          </button>
        ) : (
          <p className={`text-center text-sm sm:text-right ${helperTone === 'error' ? 'text-red-700' : 'text-gray-600'}`}>{helperText}</p>
        )}
      </div>
    ) : null}
    <div className="mx-auto flex max-w-4xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
      <div className="order-2 flex items-center justify-between sm:order-1 sm:justify-start sm:gap-4">
        {!isFirst ? (
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {backLabel}
          </button>
        ) : <span />}
        {saveStatus ? <SaveStatus status={saveStatus} lastSavedAt={lastSavedAt} onRetry={onRetrySave} className="sm:ml-2" /> : null}
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={isSubmitting || nextDisabled}
        className={`order-1 inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-base font-semibold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:order-2 sm:w-auto sm:py-2.5 sm:text-sm ${
          nextVariant === 'success'
            ? 'bg-green-700 text-white hover:bg-green-800 focus:ring-green-500'
            : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
        }`}
      >
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {nextLabel}
        {!isSubmitting && nextVariant !== 'success' && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  </div>
);

export default StepFooter;
